import { ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Button, Row } from "reactstrap";
import { flushSync } from "react-dom";
import classNames from "classnames";
import { keys } from "underscore";

import {
  FormBuilderButtonStyles,
  FormBuilderField,
  PublicForm,
} from "../types/FormBuilderInterfaces";
import useSubmitPublicForm from "../api/api-hooks/publicForms/useSubmitPublicForm";
import GCLogo from "../assets/img/growthcloud/growthcloud_wordmark.svg";
import AppHelmet from "../components/AppComponents/AppHelmet";
import useForm from "../api/api-hooks/publicForms/useForm";
import SkeletonLoader from "../elements/SkeletonLoader";
import { getTextColor } from "../utils/DashboardUtils";
import InstanceUtils from "../utils/InstanceUtils";
import { AppConfirm } from "../utils/PopupUtil";
import FieldUtils from "../utils/FieldUtils";
import AppAlert from "../elements/AppAlert";
import FormField from "./FormField";

function FormLayout() {
  const { formID, tenantID }: any = useParams();
  const { form, module, formLoading } = useForm(formID, tenantID);
  const { submitForm, submittingForm } = useSubmitPublicForm();
  const isIframe = useIFrameQuery();

  const [reset, setReset] = useState(0);
  const [info, setInfo] = useState<any>({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fields = keys(form?.Fields);
  const hasSections = form?.Layout?.sections?.length
    ? form?.Layout?.sections?.length > 0
    : false;

  const formModule = useMemo(() => {
    const formFields = form?.Fields;

    if (!formFields) return { Fields: [] };

    const fields = keys(form.Fields);

    return {
      Fields: fields.map((f) => {
        if (formFields[f].Settings?.Required) {
          return { ...formFields[f].Field, Required: true };
        }
        return formFields[f].Field;
      }),
    };
  }, [form?.Fields]);

  useEffect(() => {
    if (!form || !form.Fields || !Object.keys(form.Fields).length) return;
    for (const fieldAPIName of Object.keys(form.Fields)) {
      const field = form.Fields[fieldAPIName];
      const defaultValue = field.Settings?.Default;
      if (defaultValue) {
        setInfo((prev: any) => ({ ...prev, [fieldAPIName]: defaultValue }));
      }
    }
  }, [form]);

  if (formLoading) return <FormLoading isIframe={isIframe} />;
  if (!form || !form?.Fields) return <FormNotFound isIframe={isIframe} />;

  const onChange = (APIName: string, value: any) => {
    setInfo((prev: any) => {
      if (!value) {
        delete prev[APIName];
        return { ...prev };
      }

      return { ...prev, [APIName]: value };
    });
  };

  const saveInfo = async () => {
    flushSync(() => setError(""));
    flushSync(() => setSuccess(""));

    if (form.Fields === undefined) return;

    const containsRequired = FieldUtils.containsRequiredFields(
      formModule,
      info
    );

    if (!Object.keys(info).length) {
      return setError(
        "Failed to submit. Ensure you have at least one field filled out!"
      );
    }

    if (!containsRequired) {
      return setError(
        "Failed to submit. Ensure you have all required fields filled out!"
      );
    }

    const error = ValidateInfo(form.Fields, info);

    if (error) return setError(error);

    const payload = await InstanceUtils.formatRecordCreationPayload(
      formModule?.Fields,
      info
    );

    submitForm({
      formID: formID,
      tenantID: tenantID,
      payload: payload,
      onSuccess: () => {
        setSuccess(
          form?.PostSubmit?.ThankYouMessage ??
            "Thank you for filling out this form."
        );

        setInfo({});
        setReset((prev) => prev + 1);

        if (form?.PostSubmit?.URLRedirect) {
          window.open(form.PostSubmit.URLRedirect, "_self");
        }

        window.localStorage.setItem(`submission-${form?._id}`, "true");
      },
      onError: (err) => {
        if (err === "You already have a submission for this form.") {
          setError(err);

          AppConfirm({
            title: "Submission Limit Reached",
            text: "You have already submitted this form.",
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary)",
            showCancelButton: false,
          }).then(() => {
            setError("");
            window.localStorage.setItem(`submission-${form?._id}`, "true");
          });
        } else {
          setError(err);
        }
      },
    });
  };

  const coverImage = form?.Header?.CoverImage?.PublicURL;

  return (
    <>
      <AppHelmet
        title={
          form?.PageSettings?.Title || form?.Header?.Name || "GrowthCloud Form"
        }
        description={form?.PageSettings?.Description}
        favicon={form?.PageSettings?.Favicon?.PublicURL}
      />
      <FormWrapper isIframe={isIframe} form={form}>
        {!isIframe && coverImage && (
          <img
            src={coverImage}
            className="mt-4 rounded-sm"
            style={{
              height: 250,
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        )}
        {form.Header?.Title && (
          <h2
            className={isIframe ? "mt-0" : "mt-4"}
            style={{ color: form?.GeneralStyles?.TitleColor ?? "var(--dark)" }}
          >
            {form.Header?.Title}
          </h2>
        )}
        <div
          className="mb-4"
          style={{
            color: form?.GeneralStyles?.DescriptionColor ?? "var(--medium)",
          }}
        >
          {form.Header?.Description}
        </div>
        {hasSections ? (
          form.Layout.sections.map((section) => {
            return (
              <div key={section.id} className="d-flex flex-column w-full">
                <div className="d-flex flex-column mb-3">
                  <h2 className="text-dark">{section.Title}</h2>
                  <span>{section.Description}</span>
                </div>
                <Row className={`align-items-top mb-4`}>
                  {section.Fields.map((field: any, i: number) => {
                    return (
                      <FormField
                        key={i}
                        form={form}
                        field={field}
                        onChange={onChange}
                        module={module}
                        info={info}
                        reset={reset}
                      />
                    );
                  })}
                </Row>
              </div>
            );
          })
        ) : (
          <Row className={`align-items-top`}>
            {fields.map((field: any, i: number) => {
              return (
                <FormField
                  key={i}
                  form={form}
                  field={field}
                  onChange={onChange}
                  module={module}
                  info={info}
                  reset={reset}
                />
              );
            })}
          </Row>
        )}

        <div
          className={classNames("d-flex", {
            "justify-content-center": form?.ButtonStyles?.Position === "Center",
            "justify-content-start": form?.ButtonStyles?.Position === "Left",
            "justify-content-end": form?.ButtonStyles?.Position === "Right",
          })}
        >
          <FormButton
            saveInfo={saveInfo}
            submittingForm={submittingForm}
            buttonStyles={form.ButtonStyles}
          />
        </div>
        {success && <AppAlert color="success">{success}</AppAlert>}
        {error && <AppAlert color="danger">{error}</AppAlert>}
      </FormWrapper>
    </>
  );
}

function FormLoading({ isIframe }: { isIframe: boolean }) {
  if (isIframe) return null;

  return (
    <FormWrapper isIframe={false}>
      <div className="py-3">
        <SkeletonLoader height={225} css="mb-3" />
        <SkeletonLoader width={"25%"} />
        <SkeletonLoader width={"50%"} css="mb-4" />
        <SkeletonLoader height={50} css="mb-3" />
        <SkeletonLoader height={50} css="mb-3" />
        <SkeletonLoader height={50} css="mb-3" />
        <SkeletonLoader height={50} css="mb-3" />
      </div>
    </FormWrapper>
  );
}

function FormNotFound({ isIframe }: { isIframe: boolean }) {
  if (isIframe) return null;

  return (
    <FormWrapper isIframe={false}>
      <h1 className="text-center pt-5">Form Not Found</h1>
      <p className="text-center pb-4">A form with this id doesn't exist.</p>
    </FormWrapper>
  );
}

function FormWrapper(props: {
  children: ReactNode;
  form?: PublicForm;
  isIframe: boolean;
}) {
  if (props.isIframe) {
    return <IFrameFormWrapper>{props.children}</IFrameFormWrapper>;
  }

  let bgColor = props.form?.GeneralStyles?.BackgroundColor;
  const disableGC = props.form?.Header?.DisableGCLogo;
  const logoFile = props.form?.Header?.Logo?.PublicURL;
  const termsTextColor = getTextColor(
    props?.form?.GeneralStyles?.BackgroundColor || "#ffffff"
  );

  // If background is set to white, and is GrowthCloud logo, use gradient instead.
  if (!disableGC && bgColor?.toLowerCase?.() === "#ffffff") {
    bgColor = undefined;
  }

  const oneSubmissionLimit = props?.form?.SpamProtection?.LimitToOneSubmission;

  if (oneSubmissionLimit) {
    const submissionExists = window.localStorage.getItem(
      `submission-${props?.form?._id}`
    );

    if (submissionExists)
      return (
        <div
          className="tw:w-screen tw:h-screen tw:flex tw:items-center tw:justify-center"
          style={{
            backgroundColor: bgColor || undefined,
            backgroundImage: bgColor
              ? undefined
              : "linear-gradient(180deg, #fff 0%, #E4FDEF 100%)",
            backgroundAttachment: "fixed",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div
            className="tw:px-8 tw:py-4 tw:flex tw:flex-col tw:rounded tw:items-center"
            style={{
              boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
              backgroundSize: "100vh",
              backgroundColor:
                props?.form?.FormStyles?.BackgroundColor ?? "#ffffff",
            }}
          >
            <i className="mdi mdi-text-box-check-outline tw:text-primary tw:mb-4 tw:text-8xl" />
            <h2 className="tw:text-dark">Thank you!</h2>
            <p className="tw:text-dark tw:mb-0 tw:text-lg">
              Your submission has been received.
            </p>
            <p className="tw:text-sm ">
              If you need further assistance, please contact our support team.
            </p>
            <Button
              className="tw:mb-2"
              size="sm"
              color="primary"
              onClick={() => window.open("mailto:support@growthcloud.com")}
            >
              Contact Support
            </Button>
          </div>
        </div>
      );
  }

  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center py-4 px-2 px-sm-4 sm:"
      style={{
        minHeight: "100vh",
        backgroundColor: bgColor || undefined,
        backgroundImage: bgColor
          ? undefined
          : "linear-gradient(180deg, #fff 0%, #E4FDEF 100%)",
        backgroundAttachment: "fixed",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {((disableGC && logoFile) || !disableGC) && (
        <img
          src={logoFile ?? GCLogo}
          style={{ width: 180, marginRight: 50, marginLeft: 10 }}
          className="mb-4"
        />
      )}
      <div
        className="px-3 px-sm-4 d-flex flex-column rounded"
        style={{
          boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
          maxWidth: 800,
          width: "100%",
          backgroundSize: "100vh",
          backgroundColor:
            props?.form?.FormStyles?.BackgroundColor ?? "#ffffff",
        }}
      >
        {props.children}
      </div>
      <p className="small pt-4 px-3" style={{ color: termsTextColor }}>
        By using this form you accept the{" "}
        <a className="font-weight-bold cursor-pointer text-dark" href="/terms">
          Terms and Conditions
        </a>{" "}
        and you agree with our{" "}
        <a
          className="font-weight-bold cursor-pointer text-dark"
          href="/privacy"
        >
          Privacy Policy
        </a>
      </p>
    </div>
  );
}

function FormButton({
  saveInfo,
  submittingForm,
  buttonStyles,
}: {
  saveInfo: () => void;
  submittingForm: boolean;
  buttonStyles?: FormBuilderButtonStyles;
}) {
  let minWidth;

  const size = buttonStyles?.Size;

  if (size === "Medium") {
    minWidth = 250;
  }
  if (size === "Large") {
    minWidth = "100%";
  }

  return (
    <Button
      onClick={saveInfo}
      className="my-4 border"
      disabled={submittingForm}
      style={{
        minWidth,
        backgroundColor: buttonStyles?.BackgroundColor || "var(--primary)",
        color: buttonStyles?.TextColor || "#ffffff",
        borderRadius: buttonStyles?.BorderRadius
          ? `${buttonStyles?.BorderRadius}px`
          : undefined,
      }}
    >
      {submittingForm ? "Submitting..." : buttonStyles?.ButtonText ?? "Submit"}
    </Button>
  );
}

function IFrameFormWrapper(props: { children: ReactNode }) {
  return (
    <div className="bg-transparent" style={{ overflowX: "hidden" }}>
      {props.children}
    </div>
  );
}

function useIFrameQuery() {
  const { search } = useLocation();

  return !!search?.includes?.("iframe=true");
}

function ValidateInfo(
  fields: {
    [key: string]: FormBuilderField;
  },
  info: any
) {
  const keys = Object.keys(info);

  let error;

  for (const field of keys) {
    const schema = fields[field].Field;
    // Can be used for values validation in the future
  }

  return error;
}

export default FormLayout;
